import { Component, EventEmitter, Input, OnInit, OnChanges, OnDestroy, Output, SimpleChanges } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { SAVE_DEBOUNCE_MILLIS } from "src/app/constants/sale";
import { Contact } from "src/app/models/contact.model";
import { MasterService } from "src/app/services/master.service";
import { MessageService } from "src/app/services/message.service";
import { contactIcon } from "src/app/utils/icons";
import { Subscription } from 'rxjs';

@Component({
    selector: 'contact-modal',
    templateUrl: './contact-modal.component.html',
    styleUrls: ['./contact-modal.component.scss'],
    standalone: false
})

export class ContactModalComponent implements OnInit, OnChanges, OnDestroy {

    /** */
    @Input() contactId: number | null = null

    /** In order to have multiple components on a single page (tabs, etc...) we need a key to uniquely identify each one. */
    @Input() key: string | null = null

    /** */
    @Input() language: string = 'EN'

    /** */
    @Input() words = {}

    /** */
    @Output() modified: EventEmitter<Object> = new EventEmitter()

    protected form: FormGroup | null = null

    /** Meta data for the current contact. */
    protected contact: Contact | null = null

// A debounce timer for input change events. Used to wait for 
    // the user to finish typing before performing an action.
    private watcher: ReturnType<typeof setTimeout> | null = null

    private subscriptions: Subscription[] = []

    get icon() {
        return contactIcon(null)
    }

    get individual(): boolean {
        return (this.contact && this.contact.isIndividual)
    }

    get lastFour(): string {
        if (!this.individual) {
            return ''
        }

        const taxId = this.form.get('taxId').value
        if (!taxId || (taxId.length < 4)) {
            return ''
        }

        return taxId.substring(taxId.length-4)
    }

    get display() {
        if (!this.contact) {
            return ''
        }

        if (!this.contact.isIndividual) {
            return this.contact.name
        }

        return `${this.contact.title || ''} ${this.contact.firstName} ${this.contact.lastName} ${this.contact.suffix || ''}`.trim()
    }

    constructor(private master: MasterService, private ms: MessageService, private fb: FormBuilder) {
        this.form = this.fb.group({
            dob: new FormControl<string | null>(null, []),
            taxId: new FormControl<string | null>(null, [])
        })
    }

    ngOnInit(): void {
        this.fetch()

        this.subscriptions.push(
            this.form.valueChanges.subscribe((_) => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }

                this.watcher = setTimeout(() => {
                    this.save()
                }, SAVE_DEBOUNCE_MILLIS)
            })
        )
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }

        if (changes.contactId) {
            this.fetch()
        }
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
        if (this.watcher) {
            clearTimeout(this.watcher)
        }
    }

    private fetch = (): void => {
        this.contact = null
        
        if (!this.contactId || (this.contactId <= 0)) {
            return
        }
        
        this.master.get(`contacts/${this.contactId}`, (res) => {  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data.contact) {
                return
            }
            
            this.contact = res.data.contact

            this.form.patchValue({
                dob: this.contact.birthdate,
                taxId: this.contact.taxPayerNumber
            }, {emitEvent: false})
        })
    }

    private save = (): void => {
        if (!this.contact || !this.contactId || (this.contactId <= 0)) {
            return
        }

        this.contact.birthdate = this.form.get('dob').value,
        this.contact.taxPayerNumber = this.form.get('taxId').value
        
        this.master.put(`contacts/${this.contactId}`, {information: this.contact}, (res) => {  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
        })
    }

    /**
     * Show/hide the tax identifier.
     * @param e {Event} 
     */
    protected toggleReveal = (e: Event) => {
        const input = document.querySelector('input#contact-tax-id') as HTMLInputElement
        if (!input) {
            return
        }

        if (input.type === 'password') {
            input.type = 'text'
            return
        }

        input.type = 'password'
    }

    /**
     * Contact was modified in the modal.
     * @param {Object} meta Details about the contact modification.
     */
    protected contactModified = (meta: Object) => {
        this.modified.emit(meta)
    }
}
